interface AIRiskBadgeProps {
  score?: number; // 0-10
  urgencyLevel?: 'critical' | 'high' | 'medium' | 'low';
  importanceLevel?: 'critical' | 'high' | 'medium' | 'low';
  className?: string;
  showScore?: boolean;
}

/**
 * Displays an AI-powered risk or priority badge
 * Can show CVE risk score or patch priority score with urgency/importance level
 */
export function AIRiskBadge({
  score,
  urgencyLevel,
  importanceLevel,
  className = '',
  showScore = true,
}: AIRiskBadgeProps) {
  const level = urgencyLevel || importanceLevel;

  if (!score && !level) {
    return (
      <span
        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 ${className}`}
      >
        <span>⏳</span>
        <span>Analysis Pending</span>
      </span>
    );
  }

  // Determine color scheme based on level
  const getColorClasses = (): string => {
    switch (level) {
      case 'critical':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border-red-300 dark:border-red-700';
      case 'high':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border-orange-300 dark:border-orange-700';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700';
      case 'low':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-300 dark:border-green-700';
      default:
        // Score-based coloring if no level
        if (score) {
          if (score >= 9)
            return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 border-red-300 dark:border-red-700';
          if (score >= 7)
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 border-orange-300 dark:border-orange-700';
          if (score >= 4)
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 border-yellow-300 dark:border-yellow-700';
          return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 border-green-300 dark:border-green-700';
        }
        return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300 border-gray-300 dark:border-gray-700';
    }
  };

  const getIcon = (): string => {
    switch (level) {
      case 'critical':
        return '🚨';
      case 'high':
        return '⚠️';
      case 'medium':
        return '⚡';
      case 'low':
        return '✓';
      default:
        return '🤖';
    }
  };

  const getLabel = (): string => {
    if (level) {
      return level.charAt(0).toUpperCase() + level.slice(1);
    }
    if (score !== undefined) {
      if (score >= 9) return 'Critical';
      if (score >= 7) return 'High';
      if (score >= 4) return 'Medium';
      return 'Low';
    }
    return 'Unknown';
  };

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold rounded-md border ${getColorClasses()} ${className}`}
      title={score !== undefined ? `AI Score: ${score.toFixed(1)}/10` : undefined}
    >
      <span aria-hidden="true">{getIcon()}</span>
      <span>{getLabel()}</span>
      {showScore && score !== undefined && (
        <span className="ml-0.5 opacity-75">({score.toFixed(1)})</span>
      )}
    </span>
  );
}
